import { useState } from "react";
import { toast } from "sonner";
import { QRCodeCanvas } from "qrcode.react";
import { DownloadSimple, QrCode, Copy } from "@phosphor-icons/react";
import { FieldGroup } from "./shared";

/* ============================================================================
 * QrCodeSection — generate a downloadable QR pointing to a campaign URL.
 * ==========================================================================*/
export function QrCodeSection() {
  const [url, setUrl] = useState("https://stableish.me/qr");
  const [label, setLabel] = useState("stableish");

  const onDownload = () => {
    const canvas = document.querySelector('[data-testid="qr-canvas"] canvas');
    if (!canvas) {
      toast.error("QR canvas not ready.");
      return;
    }
    const dataUrl = canvas.toDataURL("image/png");
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `${(label || "stableish").replace(/[^a-z0-9-]/gi, "-").toLowerCase()}-qr.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success("QR code downloaded.");
  };

  const onCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("URL copied.");
    } catch {
      toast.error("Copy failed.");
    }
  };

  return (
    <section
      className="border border-white/10 bg-[#0a0a0a] p-6"
      data-testid="admin-qr"
    >
      <div className="flex items-center gap-3 mb-5">
        <QrCode size={18} weight="duotone" className="text-[#FF2A2A]" />
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A] mb-1">
            // qr code generator
          </div>
          <h3 className="font-display text-xl font-extrabold tracking-tight">
            Print-ready QR
          </h3>
        </div>
      </div>

      <div className="grid md:grid-cols-[1fr_auto] gap-8 items-start">
        <div className="space-y-4">
          <FieldGroup label="URL" testId="qr-url">
            <input
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              data-testid="qr-url-input"
              className="w-full bg-black border border-white/15 focus:border-[#FF2A2A] px-3 py-2 font-mono text-xs text-white focus:outline-none"
            />
          </FieldGroup>
          <FieldGroup label="Filename label" testId="qr-label">
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              data-testid="qr-label-input"
              placeholder="e.g. hospital-poster"
              className="w-full bg-black border border-white/15 focus:border-[#FF2A2A] px-3 py-2 font-mono text-xs text-white focus:outline-none"
            />
          </FieldGroup>
          <p className="text-xs text-white/50 leading-relaxed">
            Point at any campaign link — e.g. <code className="font-mono text-[#FF2A2A]">stableish.me/qr</code> so
            scans get tracked with <code className="font-mono">utm_source=qr</code> before redirecting to the App Store.
          </p>
          <div className="flex flex-wrap gap-2 pt-2">
            <button
              onClick={onDownload}
              data-testid="qr-download"
              className="inline-flex items-center gap-2 bg-[#FF2A2A] hover:bg-[#FF4D4D] text-white px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors"
            >
              <DownloadSimple size={12} weight="bold" />
              Download PNG
            </button>
            <button
              onClick={onCopyUrl}
              data-testid="qr-copy"
              className="inline-flex items-center gap-2 border border-white/15 hover:border-white/40 text-white/85 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors"
            >
              <Copy size={12} weight="bold" />
              Copy URL
            </button>
          </div>
        </div>

        <div
          data-testid="qr-canvas"
          className="bg-white p-5 self-start justify-self-center md:justify-self-end"
        >
          <QRCodeCanvas
            value={url || "https://stableish.me"}
            size={200}
            level="H"
            fgColor="#050505"
            bgColor="#ffffff"
            includeMargin={false}
          />
        </div>
      </div>
    </section>
  );
}
